
import { namespace } from 'vuex-class';
import { Vue, Component, Prop } from 'vue-property-decorator';

import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import SingleStepEventEncounter from '@/components/sports/common/SingleStepEventEncounter.vue';

import { SnowplowSportContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { Platform, SportEventV2, SportType, SraPlacement } from '@/@types/graphql-types';
import SponsoredRecommendationSports from '@/components/sponsored-recommendations/SponsoredRecommendationSports.vue';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { SingleStepContext, contextIs } from '@/helpers/sports/config';
import { toastError } from '@/helpers/toast/toast-helper';
import {
	SportEventsV2Document,
	SportEventsV2Query,
	SportEventsV2QueryVariables,
} from '@/pages/graphql/queries/getSportsEventsV2.query';
import { getAllowSponsoredRecommendations } from '@/components/sponsored-recommendations/useSponsoredRec';

const sportEventsSubheading = {
	upcoming: 'SPORTS_WEBAPP_HEADER_SPORT_EVENTS_UPCOMING',
	live: 'SPORTS_WEBAPP_HEADER_SPORT_EVENTS_LIVE',
	today: 'SPORTS_WEBAPP_HEADER_SPORT_EVENTS_TODAY',
} as const;

const competitionEventsSubheading = {
	upcoming: 'SPORTS_WEBAPP_HEADER_COMPETITION_EVENTS_UPCOMING',
	live: 'SPORTS_WEBAPP_HEADER_COMPETITION_EVENTS_LIVE',
	today: 'SPORTS_WEBAPP_HEADER_COMPETITION_EVENTS_TODAY',
} as const;

const Language = namespace('language');
const Routing = namespace('routing');

@Component({
	name: 'SingleStepEventEncounterList',
	components: {
		SingleStepEventEncounter,
		HiddenHorizontalScrollbar,
		SponsoredRecommendationSports,
	},
	apollo: {
		sponsoredAdData: {
			query: SportEventsV2Document,
			fetchPolicy: 'cache-and-network',
			variables(): SportEventsV2QueryVariables {
				return this.queryVariables;
			},
			update({ sportEventsV2 }) {
				return sportEventsV2.sponsoredAd;
			},
			errorPolicy: 'all',
			error(error) {
				captureMessageForSentry(
					'[GraphQL Get Upcoming Football Events Data]:',
					{ error: error, where: 'Component: GlobalSportsV2Cards' },
					'error'
				);
				process.client ? this.showErrorToast() : null;
			},
		},
	},
})
export default class SingleStepEventEncounterList extends Vue {
	sponsoredAdData: SportEventsV2Query['sportEventsV2']['sponsoredAd'];

	@Prop({ required: true }) context: SingleStepContext;
	@Prop({ required: true }) events: SportEventV2[];
	@Prop({ required: true }) sraPlacement: SraPlacement;
	@Prop({ required: true }) isDataLoading: boolean;
	@Prop({ required: true }) timeGroup: keyof typeof sportEventsSubheading;

	@Language.Getter country: string;
	@Language.Getter language: string;

	@Routing.Getter currentPageType: string;

	get queryVariables() {
		return {
			country: this.country,
			filter: {
				sports: [SportType.Football],
			},
			language: this.language,
			platform: Platform.Web,
			timezoneOffset: 0,
			allowSponsoredRecommendations: getAllowSponsoredRecommendations({
				pageType: this.currentPageType,
				placement: this.sraPlacement,
			}),
		};
	}

	get sponsoredAd() {
		return this.sponsoredAdData;
	}

	get sportContext() {
		return [new SnowplowSportContext(this.context.sportType, this.context.pageType)];
	}

	get subheadings() {
		return contextIs.competition(this.context) ? competitionEventsSubheading : sportEventsSubheading;
	}

	showErrorToast() {
		toastError({ message: this.$t('WEBAPP_ALERT_ERROR_TITLE') as string, duration: 2000 });
	}

	getSubheading(eventTimeGroup: keyof typeof sportEventsSubheading) {
		const isNBA = this.events[0].competition?.id === 'scn-bkb-8331232' ? true : false;
		if (isNBA) return;
		return this.subheadings[eventTimeGroup];
	}

	domainSessionId: string | null = null;

	async created() {
		this.domainSessionId = await TrackingHelper.getDomainSessionId();
	}
}
